import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import ProfileSelector from "@/components/auth/ProfileSelector";
import { getDeviceId } from "@/utils/deviceId";
// Dynamic import to keep the ~150 KB @sentry/react bundle off the
// critical path. Sentry boots lazily from main.tsx; this helper
// no-ops until that chunk has been pulled.
const setSentryUser = (
  userId: string | null,
  email: string | null,
  roles: string[] = []
): void => {
  void import("@/lib/sentryConfig")
    .then((m) => m.setSentryUser(userId, email, roles))
    .catch(() => undefined);
};
import { toast } from "sonner";
import { GA4_STANDARD_MIRROR } from "@/lib/analyticsConfig";
type AppRole = "actor" | "parent" | "coach" | "agent" | "manager" | "vendor" | "casting_professional" | "casting_associate" | "producer" | "studio" | "admin" | "writer" | "hmu_artist" | "crew_member" | "photographer";

const ACTIVE_PROFILE_KEY = 'castready_active_profile';

interface Profile {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  date_of_birth: string | null;
  age_group: string | null;
  skill_level: string | null;
  shared_email_group_id: string | null;
  is_managed_child: boolean | null;
  created_by_parent_id: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  isLoading: boolean;
  activeProfileId: string | null;
  showProfileSelector: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (
    email: string, 
    password: string, 
    firstName?: string, 
    lastName?: string, 
    dateOfBirth?: string,
    userType?: "actor" | "parent" | "professional",
    professionalType?: "coach" | "agent" | "vendor"
  ) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  hasAnyRole: (roles: AppRole[]) => boolean;
  getFullName: () => string;
  getAgeGroup: () => string;
  isMinor: () => boolean;
  isCoppaAge: () => boolean;
  switchProfile: (profileId: string) => Promise<void>;
  openProfileSelector: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Initialize from localStorage for persistence across page refreshes
  const [activeProfileId, setActiveProfileId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(ACTIVE_PROFILE_KEY);
    }
    return null;
  });
  
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  const [needsProfileSelection, setNeedsProfileSelection] = useState(false);

  const fetchProfileData = useCallback(async (profileId: string) => {
    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name, avatar_url, date_of_birth, age_group, skill_level, shared_email_group_id, is_managed_child, created_by_parent_id, guardian_required, guardian_linked, created_at")
        .eq("id", profileId)
        .single();

      if (profileError) {
        console.error("Error fetching profile:", profileError);
        return;
      }

      if (profileData) {
        setProfile(profileData);
      }

      // Fetch roles for the profile
      const { data: rolesData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", profileId);

      if (rolesData) {
        setRoles(rolesData.map((r) => r.role as AppRole));
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
    }
  }, []);

  // fetchUserData is now just an alias for fetchProfileData (BUG-015 dedup)
  const fetchUserData = fetchProfileData;

  // Sync active profile to database for multi-device support
  const syncActiveSession = useCallback(async (authUserId: string, profileId: string) => {
    try {
      const deviceId = getDeviceId();
      
      await supabase
        .from("active_sessions")
        .upsert({
          auth_user_id: authUserId,
          active_profile_id: profileId,
          device_id: deviceId,
          last_active_at: new Date().toISOString()
        }, { 
          onConflict: 'auth_user_id,device_id'
        });

      // Also mirror to active_profile_state (cross-device, server authoritative).
      // child_id is null when the auth user is acting as themselves.
      try {
        await supabase
          .from("active_profile_state")
          .upsert({
            user_id: authUserId,
            child_id: profileId === authUserId ? null : profileId,
            updated_at: new Date().toISOString(),
          }, { onConflict: "user_id" });
      } catch (e) {
        console.error("Error mirroring active_profile_state:", e);
      }
    } catch (error) {
      console.error("Error syncing active session:", error);
    }
  }, []);

  // Restore active profile from database on login
  const restoreActiveSession = useCallback(async (authUserId: string): Promise<string | null> => {
    try {
      const deviceId = getDeviceId();
      
      const { data } = await supabase
        .from("active_sessions")
        .select("active_profile_id")
        .eq("auth_user_id", authUserId)
        .eq("device_id", deviceId)
        .maybeSingle();
      
      return data?.active_profile_id || null;
    } catch (error) {
      // No session found is normal for first login
      return null;
    }
  }, []);

  // Log auth events to auth_events table
  const logAuthEvent = useCallback(async (userId: string, eventType: string, metadata?: Record<string, unknown>) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await supabase.from("auth_events" as any).insert({
        user_id: userId,
        event_type: eventType,
        user_agent: navigator.userAgent,
        metadata: metadata || null,
      });
    } catch (error) {
      console.error("Failed to log auth event:", error);
    }
  }, []);

  /**
   * BUG-015 FIX: Shared profile restoration logic.
   * Validates localStorage profile ownership, falls back to DB session, then auth user.
   * Used by both onAuthStateChange and getSession to eliminate duplication.
   */
  const restoreOrFetchProfile = useCallback(async (
    authUserId: string,
    email: string | null | undefined,
    options?: { syncSession?: boolean }
  ) => {
    const savedProfileId = localStorage.getItem(ACTIVE_PROFILE_KEY);
    const shouldSync = options?.syncSession ?? false;

    // Helper to set Sentry context after profile load
    const setSentryForProfile = async (profileId: string) => {
      const { data: rolesData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", profileId);

      // BUG-038: Check COPPA age before sending email to Sentry
      const { data: profileRow } = await supabase
        .from("profiles")
        .select("age_group")
        .eq("id", profileId)
        .single();

      // BUG-038 hardening: if we cannot resolve the profile / age_group, default to
      // *protected* — never leak email to Sentry when age is unknown.
      const isCoppaAge =
        !profileRow?.age_group ||
        ['kid', 'tween'].includes(profileRow.age_group);

      setSentryUser(
        profileId,
        isCoppaAge ? null : (email || null),
        rolesData?.map(r => r.role) || []
      );
    };

    // Case 1: Saved profile is a different user (child profile) — validate link
    if (savedProfileId && savedProfileId !== authUserId) {
      const { data: validLink } = await supabase
        .from("parent_child_links")
        .select("id")
        .eq("parent_id", authUserId)
        .eq("child_id", savedProfileId)
        .eq("status", "active")
        .maybeSingle();

      if (validLink) {
        setActiveProfileId(savedProfileId);
        await fetchProfileData(savedProfileId);
        if (shouldSync) await syncActiveSession(authUserId, savedProfileId);
        await setSentryForProfile(savedProfileId);
        return;
      } else {
        // Invalid — clear stale localStorage
        localStorage.removeItem(ACTIVE_PROFILE_KEY);
      }
    }

    // Case 2: Saved profile IS the auth user — always valid
    if (savedProfileId === authUserId) {
      setActiveProfileId(savedProfileId);
      await fetchProfileData(savedProfileId);
      if (shouldSync) await syncActiveSession(authUserId, savedProfileId);
      await setSentryForProfile(savedProfileId);
      return;
    }

    // Case 3: No saved profile — try to restore from database session
    if (shouldSync) {
      // Prefer the cross-device active_profile_state mirror, then fall back
      // to the device-scoped active_sessions record.
      let restoredProfileId: string | null = null;
      try {
        const { data: stateRow } = await supabase.rpc("get_my_active_profile_state");
        const childId = (stateRow as { ok?: boolean; child_id?: string | null } | null)?.child_id ?? null;
        if (childId) restoredProfileId = childId;
      } catch (e) {
        // Non-fatal; fall through to legacy restore.
      }
      if (!restoredProfileId) {
        restoredProfileId = await restoreActiveSession(authUserId);
      }
      if (restoredProfileId) {
        setActiveProfileId(restoredProfileId);
        localStorage.setItem(ACTIVE_PROFILE_KEY, restoredProfileId);
        await fetchProfileData(restoredProfileId);
        await setSentryForProfile(restoredProfileId);
        return;
      }
    }

    // Case 4: Default to auth user's own profile
    await fetchProfileData(authUserId);
    await setSentryForProfile(authUserId);
  }, [fetchProfileData, syncActiveSession, restoreActiveSession]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Log auth events
      if (session?.user) {
        if (event === "SIGNED_IN") {
          logAuthEvent(session.user.id, "login", { provider: session.user.app_metadata?.provider });
          // Fire `login` to GA4 + Meta. Idempotent per session via sessionStorage
          // so token refreshes don't double-count.
          try {
            const sessionKey = `cr_login_fired:${session.user.id}`;
            if (typeof window !== "undefined" && !sessionStorage.getItem(sessionKey)) {
              sessionStorage.setItem(sessionKey, "1");
              const method = session.user.app_metadata?.provider || "email";
              (window as any).gtag?.("event", "login", { method });
              (window as any).fbq?.("trackCustom", "login", { method });
            }
          } catch (e) {
            console.debug("[Analytics] login event failed", e);
          }
        } else if (event === "USER_UPDATED") {
          logAuthEvent(session.user.id, "profile_updated");
        } else if (event === "PASSWORD_RECOVERY") {
          logAuthEvent(session.user.id, "password_reset");
        }
      }

      // Defer Supabase calls with setTimeout to avoid deadlocks
      if (session?.user) {
        setTimeout(() => {
          restoreOrFetchProfile(session.user.id, session.user.email, { syncSession: true });
        }, 0);
      } else {
        setProfile(null);
        setRoles([]);
        setActiveProfileId(null);
        localStorage.removeItem(ACTIVE_PROFILE_KEY);
        setSentryUser(null, null, []);
      }

      if (event === "INITIAL_SESSION") {
        setIsLoading(false);
      }
    });

    // THEN check for existing session.
    // SECURITY: bootstrap with getUser() (round-trips to Supabase Auth and
    // validates the JWT against the auth server) rather than getSession()
    // which trusts the locally-cached token in localStorage. A tampered or
    // expired token will fail here instead of silently authenticating the
    // session as the impersonated user. See Week 1 launch-readiness audit.
    (async () => {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        // No valid session — make sure local state reflects logged-out.
        setSession(null);
        setUser(null);
        setIsLoading(false);
        return;
      }
      // Token is valid — now safe to read the cached session for tokens etc.
      const { data: sessionData } = await supabase.auth.getSession();
      setSession(sessionData.session);
      setUser(userData.user);
      await restoreOrFetchProfile(userData.user.id, userData.user.email);
      setIsLoading(false);
    })();

    return () => subscription.unsubscribe();
  }, [fetchProfileData, restoreOrFetchProfile, syncActiveSession, restoreActiveSession]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (
    email: string, 
    password: string, 
    firstName?: string, 
    lastName?: string, 
    dateOfBirth?: string,
    userType?: "actor" | "parent" | "professional",
    professionalType?: "coach" | "agent" | "vendor"
  ) => {
    // Check kill switch for new signups
    try {
      const { data: killSwitch } = await supabase
        .from('system_kill_switches')
        .select('is_enabled, user_message')
        .eq('switch_key', 'new_signups_disabled')
        .maybeSingle();
      
      if (killSwitch?.is_enabled) {
        toast.error(killSwitch.user_message || "New signups are temporarily disabled. Please try again later.");
        return { error: new Error("Signups are temporarily disabled") };
      }
    } catch (e) {
      console.error("Kill switch check failed:", e);
      // Continue with signup if kill switch check fails
    }

    const redirectUrl = `${window.location.origin}/complete-signup`;
    
    // Fire `sign_up_start` to GA4 + Meta BEFORE the network call so we can
    // measure form abandonment between start and complete. Consent Mode v2
    // gates delivery; safe to call unconditionally.
    try {
      const startParams = {
        method: "email",
        account_type: userType ?? "actor",
        professional_type: professionalType,
      };
      if (typeof window !== "undefined") {
        (window as any).gtag?.("event", "sign_up_start", startParams);
        (window as any).fbq?.("track", "Lead", startParams);
      }
    } catch (e) {
      console.debug("[Analytics] sign_up_start failed", e);
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          last_name: lastName,
          date_of_birth: dateOfBirth,
          user_type: userType,
          professional_type: professionalType,
        },
      },
    });

    // If signup successful and we have a user, assign appropriate roles
    if (!error && data.user) {
      const userId = data.user.id;
      
      // Delete the default 'actor' role if user is not an actor
      if (userType !== "actor") {
        await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", "actor");
      }
      
      // Assign role based on user type
      if (userType === "parent") {
        await supabase.from("user_roles").upsert({ user_id: userId, role: "parent" }, { onConflict: "user_id,role" });
      } else if (userType === "professional" && professionalType) {
        await supabase.from("user_roles").upsert({ user_id: userId, role: professionalType }, { onConflict: "user_id,role" });
      }
      // For "actor" type, the default trigger already assigns 'actor' role

      // Fire transactional welcome email (B2C kid/adult, parent notice, or B2B
      // studio). Best-effort; failures must not block signup.
      try {
        await supabase.functions.invoke("on-signup-welcome", {
          body: { user_id: userId },
        });
      } catch (e) {
        console.debug("[welcome] on-signup-welcome dispatch failed", e);
      }

      // Fire `sign_up_complete` to GA4 + Meta. Consent Mode v2 (configured in
      // AnalyticsProvider) gates this — denied analytics_storage drops the
      // event, so we don't need to re-check consent here.
      try {
        const params = {
          method: "email",
          account_type: userType ?? "actor",
          professional_type: professionalType,
        };
        if (typeof window !== "undefined") {
          (window as any).gtag?.("event", "sign_up_complete", params);
          (window as any).fbq?.("track", "CompleteRegistration", params);
          // Mirror to GA4 standard `sign_up` event so it appears in
          // GA4 conversions/key-events reporting. AuthContext is outside
          // the React tree so we replicate the GA4_STANDARD_MIRROR step
          // that `useExternalAnalytics.trackBoth` performs.
          const mirror = GA4_STANDARD_MIRROR["sign_up_complete"];
          if (mirror) {
            (window as any).gtag?.("event", mirror.name, { ...params, ...(mirror.extra ?? {}) });
          }
        }
      } catch (e) {
        console.debug("[Analytics] sign_up_complete failed", e);
      }
    }

    return { error };
  };

  const signOut = async () => {
    // Log logout event before clearing state
    if (user) {
      await logAuthEvent(user.id, "logout");
      
      const deviceId = getDeviceId();
      await supabase
        .from("active_sessions")
        .delete()
        .eq("auth_user_id", user.id)
        .eq("device_id", deviceId);
    }
    
    await supabase.auth.signOut();
    setProfile(null);
    setRoles([]);
    setActiveProfileId(null);
    setShowProfileSelector(false);
    setNeedsProfileSelection(false);
    localStorage.removeItem(ACTIVE_PROFILE_KEY);
  };

  const switchProfile = async (profileId: string) => {
    setActiveProfileId(profileId);
    localStorage.setItem(ACTIVE_PROFILE_KEY, profileId);
    setShowProfileSelector(false);
    setNeedsProfileSelection(false);
    
    // Sync to database for multi-device support
    if (user) {
      await syncActiveSession(user.id, profileId);
    }
    
    // Fetch the selected profile's data
    await fetchProfileData(profileId);
  };

  const openProfileSelector = () => {
    setShowProfileSelector(true);
  };

  const handleProfileSelected = (profileId: string) => {
    switchProfile(profileId);
  };

  // Check if user needs to select a profile (shared email)
  useEffect(() => {
    const checkSharedEmail = async () => {
      if (!user?.email || !session) return;
      
      try {
        // Check if this email has a shared group
        const { data: groupData } = await supabase
          .from("shared_email_groups")
          .select("id")
          .eq("email", user.email)
          .maybeSingle();

        if (groupData) {
          // Check how many profiles exist in this group
          const { data: profiles } = await supabase
            .from("profiles")
            .select("id")
            .eq("shared_email_group_id", groupData.id);

          if (profiles && profiles.length > 1 && !activeProfileId) {
            setNeedsProfileSelection(true);
            setShowProfileSelector(true);
          }
        }
      } catch (error) {
        console.error("Error checking shared email:", error);
      }
    };

    if (user && !isLoading && !activeProfileId) {
      checkSharedEmail();
    }
  }, [user, session, isLoading, activeProfileId]);

  const hasRole = (role: AppRole) => roles.includes(role);

  const hasAnyRole = (checkRoles: AppRole[]) =>
    checkRoles.some((role) => roles.includes(role));

  const getFullName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return profile?.first_name || profile?.last_name || "User";
  };

  const getAgeGroup = (): string => {
    return profile?.age_group || "adult";
  };

  const isMinor = (): boolean => {
    // Default to protected when we don't yet know — caller code (PII gating,
    // messaging, marketing emails) must err on the safe side.
    if (!profile) return true;
    if (!profile.date_of_birth) return false;
    const dob = new Date(profile.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age < 18;
  };

  const isCoppaAge = (): boolean => {
    if (!profile) return true;
    if (!profile.date_of_birth) return false;
    const dob = new Date(profile.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age < 13;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        roles,
        isLoading,
        activeProfileId,
        showProfileSelector,
        signIn,
        signUp,
        signOut,
        hasRole,
        hasAnyRole,
        getFullName,
        getAgeGroup,
        isMinor,
        isCoppaAge,
        switchProfile,
        openProfileSelector,
      }}
    >
      {children}
      {/* Profile Selector Modal for shared email accounts */}
      {user && needsProfileSelection && (
        <ProfileSelector 
          user={user} 
          onProfileSelected={handleProfileSelected} 
        />
      )}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access authentication state and methods.
 * @returns Auth context with user, session, profile, roles, and auth methods (signIn, signUp, signOut, hasRole, etc.)
 * @throws Error if used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
